import { Button, TextField, Box, Alert } from '@mui/material';
import { useState } from 'react';
import {} from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { useQueryClient } from '@tanstack/react-query';

interface InviteUserFormProps {
  data: {
    portfolio_company_id: string;
    portfolio_company_name: string;
  };
  tenantId: string;
  onSuccess: (result: { user_id: string; email: string }) => void;
  onClose: () => void;
}

export function InviteUserForm({ data, tenantId, onSuccess, onClose }: InviteUserFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !firstName || !lastName || !currentUser) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

      const response = await fetch(`${apiUrl}/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          title: title.trim() || undefined,
          tenant_id: tenantId,
          portfolio_company_id: data.portfolio_company_id,
          invited_by: currentUser.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to invite user');
      }

      const user = await response.json();

      // Invalidate users queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['users'] });

      onSuccess({ user_id: user.id, email: user.email });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      <TextField autoFocus label="First Name" fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" required />

      <TextField label="Last Name" fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" required />

      <TextField label="Email Address" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" required />

      <TextField
        label="Title"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="CFO, Controller, VP Finance, etc."
        helperText="Optional - User's job title at their company"
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={isLoading || !email || !firstName || !lastName}>
          {isLoading ? 'Inviting...' : 'Invite User'}
        </Button>
      </Box>
    </Box>
  );
}
